import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Card, CardContent, Grid2 as Grid,
  Alert, CircularProgress, Chip, Paper, Table, TableHead, TableBody, TableRow,
  TableCell, LinearProgress, IconButton, Tooltip,
} from '@mui/material';
import { Refresh, Save, PointOfSale, WarningAmber } from '@mui/icons-material';
import { sispService, type PosStatsResponse, type PosStatsRow } from '../../../../services/sisp.service';

const cve = (v: number) => `${Math.round(v || 0).toLocaleString('pt-PT')}$`;

/** Cor do estado consoante a % do limite. */
function nivel(pct: number | null): { color: 'success' | 'warning' | 'error' | 'default'; label: string } {
  if (pct == null) return { color: 'default', label: 'sem limite' };
  if (pct >= 95) return { color: 'error', label: 'crítico' };
  if (pct >= 80) return { color: 'warning', label: 'a aproximar' };
  return { color: 'success', label: 'ok' };
}

const PosVendas: React.FC = () => {
  const hoje = new Date(Date.now() - 3600e3).toISOString().slice(0, 10); // hoje em CV (UTC-1)
  const [date, setDate] = useState(hoje);
  const [data, setData] = useState<PosStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await sispService.getPosStats(date);
      setData(d);
      setEdits({});
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const saveLimite = async (row: PosStatsRow) => {
    const raw = edits[row.posId];
    const limite = raw === '' || raw == null ? null : Number(raw.replace(/[^0-9]/g, ''));
    setSavingId(row.posId);
    try {
      await sispService.setPosLimite(row.posId, limite);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Erro ao gravar limite');
    } finally {
      setSavingId(null);
    }
  };

  const alertas = (data?.pos || []).filter(p => p.pct != null && p.pct >= 80);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <PointOfSale sx={{ color: 'primary.main' }} />
        <Typography variant="h5" fontWeight={700}>Vendas por POS</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Total pago por POS ID no dia, para vigiar os limites diários da SISP. Mete o limite de cada POS e vê a % em tempo real.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {alertas.length > 0 && (
        <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }}>
          {alertas.length} POS a aproximar-se do limite diário: {alertas.map(a => `${a.posId} (${a.pct}%)`).join(', ')}
        </Alert>
      )}

      {/* Controlo */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField fullWidth type="date" label="Dia" value={date}
                onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <Button fullWidth variant="outlined" startIcon={<Refresh />} onClick={load} sx={{ height: 56 }}>
                Atualizar
              </Button>
            </Grid>
            {data && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ display: 'flex', gap: 3, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">Total do dia (todos)</Typography>
                    <Typography variant="h6" fontWeight={700}>{cve(data.totalDia.total)}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">Sem POS (carteira/numerário)</Typography>
                    <Typography variant="h6" fontWeight={700}>{cve(data.semPos.total)}</Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>POS ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Serviço</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Banco</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Vendas hoje</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Transações</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Limite diário (CVE)</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>% do limite</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.pos || []).map((row) => {
                const nv = nivel(row.pct);
                const editVal = edits[row.posId] ?? (row.limite != null ? String(row.limite) : '');
                return (
                  <TableRow key={row.posId} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {row.posId}
                      {row.ambiente && <Chip size="small" label={row.ambiente} sx={{ ml: 1 }} variant="outlined" />}
                    </TableCell>
                    <TableCell>{row.servico || <span style={{ color: '#aaa' }}>—</span>}</TableCell>
                    <TableCell>{row.banco || <span style={{ color: '#aaa' }}>—</span>}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{cve(row.total)}</TableCell>
                    <TableCell align="right">{row.nTransacoes}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TextField
                          size="small" type="number" placeholder="—"
                          value={editVal}
                          onChange={(e) => setEdits(s => ({ ...s, [row.posId]: e.target.value }))}
                          sx={{ width: 120 }}
                          inputProps={{ min: 0, step: 10000 }}
                        />
                        <Tooltip title="Gravar limite">
                          <span>
                            <IconButton size="small" color="primary" onClick={() => saveLimite(row)}
                              disabled={savingId === row.posId}>
                              {savingId === row.posId ? <CircularProgress size={16} /> : <Save fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {row.pct == null ? (
                        <Typography variant="caption" color="text.secondary">sem limite definido</Typography>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(row.pct, 100)}
                              color={nv.color === 'default' ? 'primary' : nv.color}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          </Box>
                          <Chip size="small" color={nv.color === 'default' ? undefined : nv.color} label={`${row.pct}%`} />
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {data && data.pos.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Sem vendas por POS neste dia.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
        O dia é contado pela hora de Cabo Verde. "Sem POS" junta os pagamentos pagos que não passaram por um terminal SISP (carteira, numerário). O limite diário é o que a SISP impõe a cada POS — quando se aproxima (≥80%) convém distribuir por outro POS.
      </Typography>
    </Box>
  );
};

export default PosVendas;
